import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'antd';
import { isNil } from 'lodash';
import { Modal } from 'components';
import { ReactComponent as WarningRed } from 'assets/icons/warning-red.svg';
import { Errors } from 'utils/errors';
import { Constants } from 'utils/constants';
import {
  AIDrivenSearch,
  AIDrivenSearchUpload,
  FundingFilters,
  FundingPrograms,
} from './components';
import { useAIDrivenSearch, useFundingFilters, useFundingQuery } from './hooks';
import { mapStateDataToApi } from './Funding.mapper';
import styles from './Funding.page.module.scss';

export function Funding() {
  const { state: aiState, handlers: aiHandlers } = useAIDrivenSearch();
  const {
    items,
    filters,
    clearFilters,
    updatePaginatedFilterCategory,
    exitPagination,
    exitFileSearch,
    exitFilterSearch,
  } = useFundingFilters();
  const {
    data,
    dataPaginated,
    isFetching,
    isEmpty,
    searchMode,
    searchWithFile,
    searchWithFilters,
    searchWithFiltersAndPagination,
  } = useFundingQuery();

  const [titleView, setTitleView] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);

  const isPagination = useMemo(() => titleView !== null, [titleView]);

  const handleViewAll = useCallback(
    sectionTitle => {
      setTitleView(sectionTitle);
      setPage(0);
      const newFilters = updatePaginatedFilterCategory(sectionTitle);
      const payload = mapStateDataToApi(newFilters);
      searchWithFiltersAndPagination(payload, 0, pageSize);
    },
    [searchWithFiltersAndPagination, updatePaginatedFilterCategory, pageSize]
  );

  const handleGoBack = useCallback(() => {
    setTitleView(null);
    setPage(0);
    exitPagination(); // Re-enable filters
    const payload = mapStateDataToApi();
    searchWithFilters(payload);
  }, [exitPagination, searchWithFilters]);

  const handlePaginationChange = useCallback(
    (newPage, newPageSize) => {
      setPage(newPage - 1); // Ant Design uses 1-based indexing
      setPageSize(newPageSize);
      const payload = mapStateDataToApi(filters);
      searchWithFiltersAndPagination(payload, newPage - 1, newPageSize);
    },
    [filters, searchWithFiltersAndPagination]
  );

  const handleFilterSearch = useCallback(
    payload => {
      if (isPagination === true) {
        setPage(0);
        searchWithFiltersAndPagination(payload, 0, pageSize);
      } else {
        searchWithFilters(payload);
      }
    },
    [isPagination, searchWithFilters, searchWithFiltersAndPagination, pageSize]
  );

  const handleSearchWithFile = fileId => {
    setTitleView(null);
    setPage(0);
    exitPagination(); // Re-enable filters
    searchWithFile(fileId);
  };

  /**
   * *** To actually handle disable functionality ***
   */
  useEffect(() => {
    const hasUploadedFile = !isNil(aiState.uploadedFile);
    if (hasUploadedFile === true) {
      exitFilterSearch();
    } else {
      exitFileSearch();
    }
  }, [aiState.uploadedFile, exitFileSearch, exitFilterSearch]);

  return (
    <div className={styles['funding-page-container']}>
      <section className="main-container" id="main-content">
        {/**
         * *** Sidebar::Begin ***
         */}
        <aside>
          <AIDrivenSearch
            isSearching={isFetching && searchMode === 'file'}
            onSubmit={handleSearchWithFile}
            fileId={aiState.uploadedFileId}
          >
            <AIDrivenSearchUpload
              error={aiState.error}
              disabled={
                aiState.isUploadingFile || (isFetching && searchMode === 'file')
              }
              uploadedFile={aiState.uploadedFile}
              onUpload={aiHandlers.handleFileUpload}
              onDelete={aiHandlers.handleFileDeletion}
            />
          </AIDrivenSearch>
          <FundingFilters
            isSearching={isFetching && searchMode !== 'file'}
            hasUploadedFile={!isNil(aiState.uploadedFile)}
            items={items}
            filters={filters}
            onClearFilters={clearFilters}
            onSearch={handleFilterSearch}
            isPagination={isPagination}
          />
        </aside>
        {/**
         * *** Sidebar::End ***
         */}
        <main>
          <FundingPrograms
            response={isPagination ? dataPaginated : data}
            handleViewAll={handleViewAll}
            handleGoBack={isPagination ? handleGoBack : null}
            loading={isFetching}
            sectionTitle={titleView}
            page={page + 1}
            pageSize={pageSize}
            onPaginationChange={handlePaginationChange}
            isEmpty={isEmpty}
          />
          <Modal
            isVisible={aiState.isErrorUpload}
            closable={false}
            onCancel={aiHandlers.handleFileDeletion}
            width={500}
            footer={[
              <Button
                type="default"
                className={styles['full-width']}
                onClick={aiHandlers.handleFileDeletion}
              >
                {Constants.Close}
              </Button>,
            ]}
            className="invalid-modal custom-modal warning-modal"
          >
            <div
              className="custom-modal__header"
              id={styles['warning-modal-header']}
            >
              <WarningRed />
              <span>{Errors.UploadError}</span>
            </div>
            <div className="custom-modal__body">
              {Errors.WrongFileUploadRequiredFields}
            </div>
          </Modal>
        </main>
      </section>
    </div>
  );
}
